import { isValidElement, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/cjs/styles/prism";

import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

import { Button, Callout, Icon, Tag } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";
import { copyHtmlToClipboard } from "@virtual-brain-lib/frontend-utils";
import type {
	AnalyticsEventDomain,
	Content,
	GeneratedDocument,
	GenerationStopped,
	SourceFile,
	VectorType,
} from "@virtual-brain-lib/types";

import MarkdownInlineSource from "./MarkdownInlineSource";
import {
	CLEAN_SPACE_BETWEEN_NUMBER_DOT_AND_TEXT_REGEX,
	markdownSanitizeSchema,
	STYLE_BLOCK_REGEX,
} from "./markdownSanitizeSchema";
import MarkdownTable from "./MarkdownTable";
import MermaidWrapper from "./MermaidWrapper";
import { preprocessHtmlBlocks } from "./preprocessMarkdownTables";

export type MarkdownProps = {
	readonly children: string;
	readonly disabled?: boolean | undefined;
	readonly contents?: Content[] | undefined;
	readonly generatedDocuments?: GeneratedDocument[] | undefined;
	readonly isWriting?: boolean | undefined;
	readonly onSourceSelected?: ((params: SourceFile) => void) | undefined;
	readonly color?: string | undefined;
	readonly isMarkdownSectionSelected?: boolean;
	readonly renderArtifact?: ((id: string) => ReactNode) | undefined;
	readonly analyticsEventDomain?: AnalyticsEventDomain | undefined;
};

export default function Markdown({
	children,
	contents,
	generatedDocuments,
	disabled,
	isWriting,
	onSourceSelected,
	isMarkdownSectionSelected,
	renderArtifact,
	analyticsEventDomain,
}: MarkdownProps) {
	const cleanedContent = preprocessHtmlBlocks(
		children
			.replace(STYLE_BLOCK_REGEX, "")
			.replace(CLEAN_SPACE_BETWEEN_NUMBER_DOT_AND_TEXT_REGEX, "$1 ")
			.replace(
				/\bexcerpt="([^"]*)"/g,
				(_match, value: string) =>
					`excerpt="${value.replace(/</g, "&lt;").replace(/>/g, "&gt;")}"`,
			),
	)
		.replaceAll("•", "-")
		.replaceAll(/([^\n])\n(#{1,6}\s)/g, "$1\n\n$2");

	return (
		<ReactMarkdown
			components={{
				aside({ ...props }) {
					const { error } = props as unknown as { error: GenerationStopped | undefined };

					if (error === "max_context_reached") {
						return (
							<Callout
								icon="exclamationTriangle"
								color="warning"
								title="We hit a context limit"
								className="my-2"
							>
								The request included more content than can be handled in one pass.
								To continue, select a specific section of the deliverable and
								iterate on it.
							</Callout>
						);
					}

					if (error === "user_stopped_generation") {
						return (
							<Callout
								icon="forbidden"
								color="warning"
								title="Generation stopped"
								className="my-2"
							>
								The generation was stopped before completion. You cannot resume it,
								but you can select a specific section to work on or regenerate from
								scratch if needed.
							</Callout>
						);
					}

					if (error === "answer_filtered") {
						return (
							<Callout
								icon="forbidden"
								color="warning"
								title="Response blocked by safety filters"
								className="my-2"
							>
								This request triggered a safety filter, so the response could not be
								completed. This can happen when certain sensitive topics are
								detected. You can try rephrasing your request or focusing on more
								general informational content before generating again.
							</Callout>
						);
					}

					return;
				},
				pre({ children, ...props }) {
					const isMermaid =
						isValidElement(children) &&
						((children.props as { className?: string }).className ?? "").includes(
							"language-mermaid",
						);

					return (
						<div className="group relative">
							{!isMermaid && (
								<Button
									size="sm"
									variant="outlineWhite"
									leftIcon="copy"
									className="absolute top-1 right-1 z-10 opacity-0 transition-opacity group-hover:opacity-100"
									tooltip={{ children: "Copy code to clipboard" }}
									onClick={() => {
										if (isValidElement(children)) {
											const props = children.props as {
												children?: React.ReactNode;
											};
											// Flatten children into an array, keep only strings, and join them into a single string
											const code = [props.children]
												.flat()
												.filter((c): c is string => typeof c === "string")
												.join("");

											copyHtmlToClipboard(
												code,
												"code",
												"Code copied to clipboard",
											);
										}
									}}
								/>
							)}

							<pre
								{...props}
								className={cn(
									"overflow-auto rounded-md",
									!isMermaid && "bg-[#282A36] p-2 text-white",
								)}
								style={{ width: "100%" }}
							>
								{children}
							</pre>
						</div>
					);
				},
				code({ className, children, ...props }) {
					const match = /language-(\w+)/.exec(className ?? "");

					const childText = typeof children === "string" ? children : "";

					if (match?.[1] === "mermaid") {
						return <MermaidWrapper isWriting={isWriting} mermaidCode={childText} />;
					}

					if (match) {
						return (
							<SyntaxHighlighter
								useInlineStyles
								showLineNumbers={false}
								style={dracula}
								language={match[1]}
								PreTag="div"
							>
								{childText.replace(/\n$/, "")}
							</SyntaxHighlighter>
						);
					}

					return (
						<code {...props} className="relative rounded-md px-1 py-0.25">
							{children}
						</code>
					);
				},
				source({ ...props }) {
					// Markdown makes all props to lowercase
					const { contentid, vectorid, page, excerpt, index, messageid, imageurl, type } =
						props as unknown as {
							excerpt: string;
							index: string;
							page: string;
							loading?: string | undefined;
							messageid?: string | undefined;
							contentid?: string | undefined;
							imageurl?: string | undefined;
							vectorid?: string | undefined;
							type?: VectorType | "excel-workbook" | undefined;
						};

					const loading = "loading" in props;

					const content = contents?.find(({ id }) => contentid === id);

					const workbook = generatedDocuments?.find(
						(document) => document.content?.id === contentid,
					);

					return (
						<MarkdownInlineSource
							key={`inline-source-${contentid}-${index}`}
							disabled={disabled}
							vectorId={vectorid}
							page={page}
							contentId={contentid}
							messageId={messageid}
							imageUrl={imageurl}
							excerpt={excerpt}
							index={index}
							content={content ?? workbook?.content}
							isLoading={Boolean(loading)}
							type={type}
							isMarkdownSectionSelected={isMarkdownSectionSelected}
							analyticsEventDomain={analyticsEventDomain}
							onSourceSelected={onSourceSelected}
						/>
					);
				},
				legend({ ...props }) {
					const { text } = props as unknown as { text: string };
					const lines = text.split("<br />");

					return (
						<Icon
							className="!py-0"
							name="infoCircle"
							tooltip={{
								children: (
									<div className="flex flex-col gap-2 !text-xs">
										{lines.map((line, i) => (
											<p key={i}>{line}</p>
										))}
									</div>
								),
								defaultPlace: "top",
								className: "!max-w-lg",
							}}
						/>
					);
				},
				// eslint-disable-next-line @typescript-eslint/promise-function-async
				object({ ...props }) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
					const { id } = props as unknown as { id: string };

					return renderArtifact?.(id) ?? null;
				},
				span({ id, children }) {
					if (id && (id.startsWith("block-") || id.startsWith("step-"))) {
						return (
							<Tag size="sm" color="primary" variant="outline" shape="pill">
								{children}
							</Tag>
						);
					}

					return <span>{children}</span>;
				},
				a({ href, children, ...props }) {
					const isUrl = href?.startsWith("http://") || href?.startsWith("https://");

					if (isUrl) {
						return (
							// eslint-disable-next-line react/jsx-no-target-blank
							<a
								href={href}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-500 underline"
								{...props}
							>
								{children}
							</a>
						);
					}

					return (
						// eslint-disable-next-line react/jsx-no-target-blank
						<a href={href} {...props}>
							{children}
						</a>
					);
				},
				// TABLE ICON INJECTION
				table: MarkdownTable,
			}}
			remarkPlugins={[remarkGfm]}
			rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
		>
			{cleanedContent}
		</ReactMarkdown>
	);
}
